import { Apollo } from 'apollo-angular';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import {
  APPLICATION_CASE_QUERY,
  APPLICATION_SERVICES_QUERY,
  APPLICATION_TIERS_QUERY,
  MY_APPLICANT_CASES_QUERY,
  MY_PURCHASED_SERVICES_QUERY
} from '../gql/queries';
import { ApplicationService, ApplicationTier, Case, MeResponse, SCReceipt } from '../gql/types';
import { catchError, filter, firstValueFrom, forkJoin, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import * as Sentry from '@sentry/browser';
import { loadStripe, PaymentIntent, Stripe, StripePaymentElementOptions } from '@stripe/stripe-js';
import { PaymentModalComponent, PaymentModalParams } from '../modals/payment-modal.component';
import { DialogService } from './dialog.service';
import { PortalExperience } from '../enums';

interface PaymentPromoCode {
  promotion?: string;
  message?: string;
  valid: boolean;
}

export interface PaymentPrices {
  listed_platform_price: string;
  platform_price_discount_dollar_amount: string;
  platform_price: string;
  listed_legal_services_price: string;
  legal_services_price_discount_dollar_amount: string;
  legal_services_price: string;
  promo_code: string;
  discount_percent_amount: string;
  total_price: string;
  cross_sell_prices?: PaymentPrices;
}

interface SelectedApplicationTier {
  applicationCaseId: string; // Currently required for any in-app purchase
  applicationTierId: string;
  serviceId?: never; // Ensures that serviceId cannot be set
}

interface SelectedService {
  applicationCaseId: string; // Currently required for any in-app purchase
  serviceId: string;
  applicationTierId?: never; // Ensures that applicationTierId cannot be set
}

export interface CreatePaymentParametersBase {
  isGiftCard?: boolean;
  paymentIntentId?: string; // Set this if possible when recreating a payment intent in the same "session"
  promoCode?: string;
  metadata?: Record<string, any>;
  crossSellServiceId?: string;
}

export type CreatePaymentParameters = CreatePaymentParametersBase & (SelectedApplicationTier | SelectedService);

interface PaymentResponse {
  intent_id: string | null;
  client_secret: string | null;
  prices: PaymentPrices;
  receipt_id: string;
  receipt_status: SCReceipt['status'];
  receipt_type: SCReceipt['receiptType'];
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private initPromise: Promise<void>;
  private paymentSuccessSubject = new Subject<void>();
  private trackingParams: Record<string, string>;

  public stripe: Stripe | null = null;
  public paymentSuccess$ = this.paymentSuccessSubject.asObservable();

  constructor(
    private authService: AuthService,
    private dialog: DialogService,
    private toastService: ToastrService,
    private http: HttpClient,
    private apollo: Apollo
  ) {
    this.initPromise = loadStripe(environment.keys.stripePublicKey).then((stripe) => {
      this.stripe = stripe;
    });

    this.checkUrlParams();

    if (this.trackingParams) {
      sessionStorage.setItem('trackingParams', JSON.stringify(this.trackingParams));
    } else {
      this.trackingParams = JSON.parse(sessionStorage.getItem('trackingParams') || '{}');
    }
  }

  async checkUrlParams() {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1]);

    const utm_source = urlParams.get('utm_source'),
      utm_medium = urlParams.get('utm_medium'),
      utm_campaign = urlParams.get('utm_campaign'),
      utm_term = urlParams.get('utm_term'),
      utm_content = urlParams.get('utm_content'),
      referral_code = urlParams.get('referralcode'), // intentional snake case conversion
      fbclid = urlParams.get('fbclid'),
      gclid = urlParams.get('gclid'),
      msclkid = urlParams.get('msclkid');

    const trackingParams = {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      referral_code,
      fbclid,
      gclid,
      msclkid
    };

    const filteredTrackingParams = Object.fromEntries(Object.entries(trackingParams).filter(([_, value]) => !!value));

    if (Object.keys(filteredTrackingParams).length > 0) {
      this.trackingParams = filteredTrackingParams;
    }

    urlParams.delete('utm_source');
    urlParams.delete('utm_medium');
    urlParams.delete('utm_campaign');
    urlParams.delete('utm_term');
    urlParams.delete('utm_content');
    urlParams.delete('referralcode');
    urlParams.delete('fbclid');
    urlParams.delete('gclid');
    urlParams.delete('msclkid');

    const clientSecret = urlParams.get('payment_intent_client_secret'),
      redirectStatus = urlParams.get('redirect_status') as PaymentIntent['status'],
      paymentIntentId = urlParams.get('payment_intent'),
      appCaseId = urlParams.get('payment_case_id'),
      promoCode = urlParams.get('payment_promo_code'),
      tierId = urlParams.get('payment_tier_id'),
      serviceSlugName = urlParams.get('payment_service_slug_name'),
      crossSellSlug = urlParams.get('payment_cross_sell_slug'),
      newPayment = urlParams.has('new_payment');

    if (!newPayment && !clientSecret) {
      // clean the url of tracking params if necessary
      if (Object.keys(filteredTrackingParams).length > 0) {
        let cleanHref = window.location.pathname;
        if (urlParams.toString()) {
          cleanHref += '?' + urlParams.toString();
        }
        window.history.pushState({}, '', cleanHref);
      }
      return;
    }

    let modalParams: PaymentModalParams;
    const appCase = appCaseId && (await this.selectApplicationCase(appCaseId));
    const tier =
      tierId && appCaseId && (await this.selectApplicationTiers(appCaseId)).find((tier) => tier.id === tierId);
    const tiers = !tierId && !serviceSlugName && appCaseId && (await this.selectApplicationTiers(appCaseId));
    const service = serviceSlugName && (await this.selectApplicationService(null, serviceSlugName));
    const crossSellService = crossSellSlug && (await this.selectApplicationService(null, crossSellSlug));
    if (tier) {
      modalParams = {
        tier,
        appCase,
        promoCode
      };
    } else if (tiers) {
      modalParams = {
        tiers,
        appCase,
        promoCode
      };
    } else if (serviceSlugName) {
      if (appCase) {
        modalParams = {
          service,
          appCase,
          promoCode,
          crossSellService
        };
      } else {
        const cases = await this.getApplicationCases();
        modalParams = {
          service,
          cases,
          promoCode,
          crossSellService
        };
      }
    }

    if (clientSecret) {
      await this.initPromise;
      const { error, paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);
      if (error) {
        console.error(error);
        this.toastService.error('Unable to verify payment result, please check back later');
        return;
      }

      if (modalParams) {
        modalParams.paymentIntentId = paymentIntent.id;
        if (paymentIntent.last_payment_error) {
          modalParams.errorMessage = paymentIntent.last_payment_error.message;
        }
      }

      await this.handlePaymentIntentResult(paymentIntentId, paymentIntent.status, undefined, modalParams);
    } else if (newPayment && modalParams) {
      // wait for the user either complete payment or dismiss the modal before we clean the url params
      const modalRef = await this.payFor(modalParams);
      await firstValueFrom(modalRef.beforeClosed());
    }

    urlParams.delete('payment_intent');
    urlParams.delete('payment_intent_client_secret');
    urlParams.delete('redirect_status');
    urlParams.delete('payment_case_id');
    urlParams.delete('payment_promo_code');
    urlParams.delete('payment_tier_id');
    urlParams.delete('payment_service_slug_name');
    urlParams.delete('payment_cross_sell_slug');
    urlParams.delete('new_payment');
    let cleanHref = window.location.pathname;
    if (urlParams.toString()) {
      cleanHref += '?' + urlParams.toString();
    }
    window.history.pushState({}, '', cleanHref);
  }

  async handlePaymentIntentResult(
    paymentIntentId: string,
    status: PaymentIntent['status'],
    modal?: PaymentModalComponent,
    modalParams?: PaymentModalParams
  ) {
    if (status.startsWith('requires_')) {
      this.toastService.warning('Please complete payment');
      if (modal) {
        modal.showError('Please complete payment');
      } else if (modalParams) {
        this.payFor(modalParams);
      }
      return;
    }
    try {
      const { status: receipt_status } = await this.updateReceiptStatus(paymentIntentId);
      this.handleReceiptStatus(receipt_status, modal);
    } catch (error) {
      console.error(error);
      this.toastService.error('Unable to verify payment result, please contact support');
      throw error;
    }
  }

  handleReceiptStatus(status: SCReceipt['status'], modal?: PaymentModalComponent) {
    if (status === 'Success') {
      this.paymentSuccessSubject.next();
      this.toastService.success('Payment succeeded!');
      if (modal) {
        modal.close();
      }
    } else if (status === 'Paid') {
      this.toastService.success('Payment received!');
      if (modal) {
        modal.close();
      }
    } else if (status === 'Canceled') {
      this.toastService.warning('Payment cancelled.');
      if (modal) {
        modal.close();
      }
    } else if (status === 'Processing') {
      this.toastService.warning('Payment processing, check back in a few days.');
      if (modal) {
        modal.close();
      }
    } else if (status === 'Error') {
      this.toastService.warning('Payment error, please contact support.');
    }
  }

  getReturnUrl({ tier, service, appCase, promoCode, crossSellService }: PaymentModalParams) {
    let href = window.location.href;
    const params = new URLSearchParams();

    if (tier) {
      params.set('payment_tier_id', tier.id);
    }
    if (service) {
      params.set('payment_service_slug_name', service.slugName);
    }
    if (appCase) {
      params.set('payment_case_id', appCase.id);
    }
    if (promoCode) {
      params.set('payment_promo_code', promoCode);
    }
    if (crossSellService) {
      params.set('payment_cross_sell_slug', crossSellService.slugName);
    }

    const queryString = params.toString();
    if (queryString) {
      href += (href.includes('?') ? '&' : '?') + queryString;
    }

    return href;
  }

  async payFor(data: PaymentModalParams) {
    await this.initPromise; // this guarantees that stripe will be ready for use in the modal
    return this.dialog.open(PaymentModalComponent, {
      data
    });
  }

  checkInterviewPrepAccess(): Observable<{ isProfessional: boolean; hasPurchased: boolean }> {
    return this.apollo.query<MeResponse>({ query: MY_PURCHASED_SERVICES_QUERY, fetchPolicy: 'network-only' }).pipe(
      map(({ data }) => {
        const activeCaseId = data?.me?.currentApplicationcase?.id;
        const receipts = data?.me?.receipts || [];
        return {
          isProfessional: receipts.some(
            (r) => r.forTier?.tierLevel === 'professional' && r.status === 'Success' && r.appCase?.id === activeCaseId
          ),
          hasPurchased: receipts.some(
            (r) => r.forService?.name?.toLowerCase().includes('interview preparation') && r.status === 'Success'
          )
        };
      }),
      tap({ error: (err) => Sentry.captureException(err) }),
      catchError(() => of({ isProfessional: false, hasPurchased: false }))
    );
  }

  private static IPK_APPLICATION_IDS: Record<string, string> = {
    '87': 'aos',
    '90': 'aos',
    '94': 'nats',
    '96': 'k1',
    '99': 'consular/?petitioner=spouse'
  };

  private static IPK_CONDITIONAL_IDS: Record<string, string> = {
    '101': 'consular/?petitioner=child',
    '102': 'consular/?petitioner=parent'
  };

  // Standalone IPK products: ApplicationService.name -> interview-prep content path.
  private static IPK_SERVICE_PATHS: Record<string, string> = {
    'Interview Preparation Kit AOS MBGC': 'aos/',
    'Interview Preparation Kit AOS PBP': 'aos/?petitioner=parent',
    'Interview Preparation Kit AOS PBC': 'aos/?petitioner=child',
    'Interview Preparation Kit Consular PBS': 'consular/?petitioner=spouse',
    'Interview Preparation Kit Consular PBP': 'consular/?petitioner=parent',
    'Interview Preparation Kit Consular PBC': 'consular/?petitioner=child',
    'Interview Preparation Kit K-1': 'k1/',
    'Interview Preparation Kit Nats': 'nats/',
    'Interview Preparation Kit ROC': 'roc/'
  };

  private getActiveCase(): Observable<Case | null> {
    return this.apollo
      .query<MeResponse>({
        query: MY_APPLICANT_CASES_QUERY,
        variables: { experienceType: PortalExperience.ForeignNational },
        fetchPolicy: 'network-only'
      })
      .pipe(
        map(({ data }) => {
          const activeCaseId = data?.me?.currentApplicationcase?.id;
          return data?.me?.appCases?.find((c) => c.id === activeCaseId) || null;
        }),
        catchError(() => of(null))
      );
  }

  getActiveInterviewPrepType(): Observable<string | null> {
    // Catch empty receipts and don't fail completely
    const receipts$ = this.apollo
      .query<MeResponse>({ query: MY_PURCHASED_SERVICES_QUERY, fetchPolicy: 'network-only' })
      .pipe(
        map(({ data }) => data?.me?.receipts || []),
        catchError(() => of([] as SCReceipt[]))
      );

    return forkJoin({
      activeCase: this.getActiveCase(),
      receipts: receipts$
    }).pipe(
      map(({ activeCase, receipts }) => {
        // A purchased standalone IPK product drives content directly (works even with no active case).
        const bought = receipts.find(
          (r) => r.status === 'Success' && r.forService?.name && PaymentService.IPK_SERVICE_PATHS[r.forService.name]
        );
        if (bought) return PaymentService.IPK_SERVICE_PATHS[bought.forService.name];

        // Fallback: case-based content (professional tier + existing service buyers).
        if (!activeCase) return null;
        const appId = activeCase.application?.id?.toString();
        if (!appId) return null;

        const directMatch = PaymentService.IPK_APPLICATION_IDS[appId];
        if (directMatch) return directMatch;

        const conditionalMatch = PaymentService.IPK_CONDITIONAL_IDS[appId];
        if (conditionalMatch && activeCase.applyingInUsa) {
          return conditionalMatch;
        }

        return null;
      })
    );
  }

  // Reverse-map an interview-prep content path (from getActiveInterviewPrepType) back to the exact
  // IPK ApplicationService name. NOrmalized to remove `/`
  static ipkServiceNameForPath(ipkType: string | null): string | undefined {
    if (!ipkType) return undefined;
    const stripSlash = (path: string) => path.replace(/\/$/, '');
    return Object.keys(PaymentService.IPK_SERVICE_PATHS).find(
      (name) => stripSlash(PaymentService.IPK_SERVICE_PATHS[name]) === stripSlash(ipkType)
    );
  }

  selectInterviewPrepService() {
    // Match IPK service based on getActiveInterviewPrepType to display appropriate IPK service for sale.
    return firstValueFrom(
      forkJoin({
        ipkType: this.getActiveInterviewPrepType(),
        services: this.apollo
          .query<{ applicationServices: ApplicationService[] }>({ query: APPLICATION_SERVICES_QUERY })
          .pipe(map(({ data }) => data.applicationServices))
      }).pipe(
        map(({ ipkType, services }) => {
          const serviceName = PaymentService.ipkServiceNameForPath(ipkType);
          return serviceName ? services.find((s) => s.name === serviceName) : undefined;
        })
      )
    );
  }

  async payForService(service: ApplicationService, cases: Case[]) {
    if (cases.length === 1) {
      return this.payFor({ appCase: cases[0], service });
    }
    return this.payFor({ cases, service });
  }

  private selectApplicationCase(caseId: string) {
    return firstValueFrom(
      this.apollo
        .query<{ case: Case }>({
          query: APPLICATION_CASE_QUERY,
          variables: {
            id: caseId
          }
        })
        .pipe(map(({ data }) => data?.case))
    );
  }

  getApplicationCases() {
    return firstValueFrom(
      this.apollo
        .query<MeResponse>({
          query: MY_APPLICANT_CASES_QUERY,
          variables: { experienceType: PortalExperience.ForeignNational }
        })
        .pipe(map(({ data }) => data?.me?.appCases))
    );
  }

  selectApplicationService(serviceID: string, serviceSlugName: string) {
    // Can match based on serviceID or serviceSlug name.
    return firstValueFrom(
      this.apollo
        .query<{ applicationServices: ApplicationService[] }>({
          query: APPLICATION_SERVICES_QUERY
        })
        .pipe(
          map(({ data }) =>
            data.applicationServices.find(
              (service) =>
                (serviceID && !serviceSlugName && service.id === serviceID) ||
                (!serviceID && serviceSlugName && service.slugName === serviceSlugName)
            )
          )
        )
    );
  }

  selectApplicationTiers(caseId: string) {
    return firstValueFrom(
      this.apollo
        .query<{ case: Case }>({
          query: APPLICATION_TIERS_QUERY,
          variables: {
            caseId
          }
        })
        .pipe(
          map(({ data }) => data?.case?.tiersForPurchase),
          filter((applicationTiers: ApplicationTier[]) => !!applicationTiers),
          map((applicationTiers) =>
            applicationTiers.filter((tier) =>
              ['basic', 'essentials', 'enhanced', 'professional'].includes(tier.tierLevel)
            )
          )
        )
    );
  }

  checkPromo(tierId: string, promoCode: string, service_id: string = '') {
    const params = {
      promo_code: promoCode
    };
    if (tierId) {
      Object.assign(params, { tier_id: tierId });
    } else if (service_id) {
      Object.assign(params, { service_id });
    }
    return firstValueFrom(
      this.http
        .get<PaymentPromoCode>(`${environment.endpoints.paymentsApi}check_promo`, {
          params
        })
        .pipe(map((response) => response))
    );
  }

  checkPrices(
    tierId: string,
    promoCode?: string,
    service_id: string = '',
    appCaseId: string = '',
    crossSellServiceId: string = ''
  ) {
    const params = {};
    if (promoCode) {
      Object.assign(params, { promo_code: promoCode });
    }
    if (appCaseId) {
      Object.assign(params, { app_case_id: appCaseId });
    }
    if (tierId) {
      Object.assign(params, { tier_id: tierId });
    } else if (service_id) {
      Object.assign(params, { service_id });
    }
    if (crossSellServiceId) {
      Object.assign(params, { cross_sell_service_id: crossSellServiceId });
    }
    return firstValueFrom(
      this.http
        .get<PaymentPrices>(`${environment.endpoints.paymentsApi}check_prices`, {
          params
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            let errorMessage = 'An unknown error occurred.';
            if (error.status === 500) {
              errorMessage = 'Internal Server Error. Please try again later.';
            } else if (error.status === 504) {
              errorMessage = 'Request Timed Out. Please try again later.';
            } else if (error.status === 400) {
              errorMessage = 'Invalid request.';
            }

            return throwError(() => new Error(errorMessage));
          })
        )
    );
  }

  updateReceiptStatus(paymentIntentId: string) {
    return firstValueFrom(
      this.http
        .post<Pick<SCReceipt, 'status'>>(`${environment.endpoints.paymentsApi}update_receipt_status/`, {
          payment_intent: paymentIntentId
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            let errorMessage = 'An unknown error occurred.';
            if (error.status === 500) {
              errorMessage = 'Internal Server Error. Please try again later.';
            } else if (error.status === 504) {
              errorMessage = 'Request Timed Out. Please try again later.';
            } else if (error.status === 400) {
              errorMessage = 'Invalid request.';
            }

            return throwError(() => new Error(errorMessage));
          })
        )
    );
  }

  getStripePaymentElementDefaultValues(): StripePaymentElementOptions['defaultValues'] {
    const name = this.authService.currentUser.first_name + ' ' + this.authService.currentUser.last_name;
    return {
      billingDetails: {
        name: name.trim() || '(No Name)', // some testing users have no name, but normal users do
        email: this.authService.currentUser.email
      }
    };
  }

  getPaymentMetadata() {
    const metadata: Record<string, string> = {
      device_type: 'desktop'
    };
    if (/Tablet|iPad|Nexus 7|Nexus 10|KFAPWI/i.test(navigator.userAgent)) {
      metadata.device_type = 'tablet';
    } else if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      metadata.device_type = 'mobile';
    } else if (navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2) {
      // this touchpoint check is necessary since iPad OS 12+
      metadata.device_type = 'tablet';
    } else {
      metadata.device_type = 'desktop';
    }

    if (!this.authService.currentUser.impersonating) {
      Object.assign(metadata, this.trackingParams);
    }

    return metadata;
  }

  createPayment(params: CreatePaymentParameters) {
    const { name: customer_name, email: customer_email } = this.getStripePaymentElementDefaultValues().billingDetails;
    const metadata = this.getPaymentMetadata();
    if (params.metadata) {
      Object.assign(metadata, params.metadata);
    }
    const payload = {
      app_case: params.applicationCaseId,
      customer_email,
      customer_name,
      for_service: null,
      for_tier: null,
      cross_sell_service: null,
      is_gift_card: params.isGiftCard || false,
      payment_intent: params.paymentIntentId,
      promo_code: params.promoCode || null,
      metadata
    };
    if (params.applicationTierId) {
      payload.for_tier = params.applicationTierId;
    } else if (params.serviceId) {
      payload.for_service = params.serviceId;
    }
    if (params.crossSellServiceId) {
      payload.cross_sell_service = params.crossSellServiceId;
    }
    return firstValueFrom(
      this.http.post<PaymentResponse>(`${environment.endpoints.paymentsApi}payment/`, payload).pipe(
        catchError((error: HttpErrorResponse) => {
          let errorMessage = 'An unknown error occurred.';
          if (error.status === 500) {
            errorMessage = 'Internal Server Error. Please try again later.';
          } else if (error.status === 504) {
            errorMessage = 'Request Timed Out. Please try again later.';
          } else if (error.status === 400) {
            errorMessage = 'Invalid request.';
          }

          return throwError(() => new Error(errorMessage));
        })
      )
    );
  }
}
